/**
 * useDataState — globaler Hook für jede Datenoperation in V5.
 *
 * Wraps TanStack Query mit:
 * - Hartem Timeout (default 12 s) via AbortController
 * - 'stale'-Status (Daten älter als ttl_seconds, aber noch da)
 * - 'no_data'-Status (Erfolg, aber leere Antwort = "Heute kein Signal")
 * - Strukturiertem DataStateError mit retry_possible-Flag
 *
 * SSoT: docs/datenmodell.md § 5 + src/types/data-state.ts.
 */

import { useQuery, type UseQueryOptions, type QueryKey } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { DataState, DataStateError } from '@/types';

/**
 * Default-Timeout in Millisekunden — passt zur Vision-Vorgabe "8-12 s".
 */
const DEFAULT_TIMEOUT_MS = 12_000;

export interface UseDataStateOptions<TData> {
  /** Stable query key, üblicherweise ['watchlist', userId, layer] o.ä. */
  queryKey: QueryKey;
  queryFn: (signal: AbortSignal) => Promise<TData | null>;
  /** Default 12 s. */
  timeoutMs?: number;
  /** Default 5 Min — danach 'stale' */
  ttlSeconds?: number;
  /** Aktiviert die Query (z.B. bei abhängigen IDs) */
  enabled?: boolean;
  /** Treatment für leere Resultate. Default: 'no_data', wenn null oder leeres Array. */
  isEmpty?: (data: TData) => boolean;
  /** Optionale weitere TanStack-Query-Optionen */
  queryOptions?: Omit<
    UseQueryOptions<TData | null, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >;
}

export interface UseDataStateResult<TData> {
  state: DataState<TData>;
  refetch: () => void;
  isFetching: boolean;
}

/**
 * Wraps queryFn in AbortController + Timeout + Empty-Check.
 * Wirft bei Timeout, sodass TanStack Query den Fehler aufnimmt.
 */
function withTimeout<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number
): (signal: AbortSignal) => Promise<T> {
  return async (parentSignal: AbortSignal) => {
    const controller = new AbortController();
    const onParentAbort = () => controller.abort(parentSignal.reason);
    parentSignal.addEventListener('abort', onParentAbort, { once: true });

    const timer = setTimeout(() => {
      controller.abort(new TimeoutError(timeoutMs));
    }, timeoutMs);

    try {
      return await fn(controller.signal);
    } finally {
      clearTimeout(timer);
      parentSignal.removeEventListener('abort', onParentAbort);
    }
  };
}

class TimeoutError extends Error {
  readonly code = 'TIMEOUT';
  constructor(timeoutMs: number) {
    super(`Anfrage hat nach ${(timeoutMs / 1000).toFixed(0)} s nicht geantwortet.`);
  }
}

function defaultIsEmpty(data: unknown): boolean {
  if (data === null || data === undefined) return true;
  if (Array.isArray(data)) return data.length === 0;
  // W67 BUGFIX 2026-05-25 — Map/Set haben keine enumerable own properties,
  // Object.keys() liefert immer []. Vorher: jede Map(46) wurde als "leer"
  // klassifiziert → state.data=null → aiResearchMap in Watchlist war stets
  // leer → Datenvertrauen-Coverage fiel von erwartet ~80% auf gemessen ~50%.
  // Folge: Aller Watchlist-Eintraege zeigten "Datenvertrauen niedrig", obwohl
  // die AI-Daten in der DB voll vorhanden waren. Symbol-Detail nutzt andere
  // Fetch-Pfade (fetchAIResearch fuer Einzel-Symbol, liefert plain Object) und
  // war daher nicht betroffen.
  if (data instanceof Map) return data.size === 0;
  if (data instanceof Set) return data.size === 0;
  if (typeof data === 'object') return Object.keys(data as object).length === 0;
  return false;
}

function toDataStateError(err: unknown): DataStateError {
  if (err instanceof TimeoutError) {
    return {
      code: err.code,
      message: err.message,
      retry_possible: true,
    };
  }
  if (err instanceof Error) {
    const status = (err as Error & { status?: number }).status;
    return {
      code: 'FETCH_FAILED',
      message: err.message,
      retry_possible: typeof status !== 'number' || status >= 500,
      status: typeof status === 'number' ? status : undefined,
    };
  }
  return {
    code: 'UNKNOWN',
    message: String(err),
    retry_possible: true,
  };
}

export function useDataState<TData>(opts: UseDataStateOptions<TData>): UseDataStateResult<TData> {
  const {
    queryKey,
    queryFn,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    ttlSeconds = 5 * 60,
    enabled = true,
    isEmpty = defaultIsEmpty,
    queryOptions = {},
  } = opts;

  const wrappedQueryFn = useMemo(
    () => withTimeout(queryFn, timeoutMs),
    [queryFn, timeoutMs]
  );

  const query = useQuery<TData | null, Error>({
    queryKey,
    queryFn: ({ signal }) => wrappedQueryFn(signal),
    enabled,
    staleTime: ttlSeconds * 1000,
    ...queryOptions,
  });

  const state: DataState<TData> = useMemo(() => {
    const lastSuccessAt = query.dataUpdatedAt ? new Date(query.dataUpdatedAt).toISOString() : null;
    const ageSec = query.dataUpdatedAt ? (Date.now() - query.dataUpdatedAt) / 1000 : Infinity;
    const isStale = lastSuccessAt !== null && ageSec > ttlSeconds;

    if (query.isPending && !query.data) {
      return {
        status: 'loading',
        data: null,
        error: null,
        last_successful_fetch_at: null,
        next_retry_at: null,
        source: 'fresh',
        ttl_seconds: ttlSeconds,
      };
    }

    if (query.isError && !query.data) {
      return {
        status: 'error',
        data: null,
        error: toDataStateError(query.error),
        last_successful_fetch_at: lastSuccessAt,
        next_retry_at: null,
        source: 'fresh',
        ttl_seconds: ttlSeconds,
      };
    }

    if (query.data !== null && query.data !== undefined && isEmpty(query.data)) {
      return {
        status: 'no_data',
        data: null,
        error: null,
        last_successful_fetch_at: lastSuccessAt,
        next_retry_at: null,
        source: 'fresh',
        ttl_seconds: ttlSeconds,
      };
    }

    if (query.data !== null && query.data !== undefined) {
      return {
        status: isStale ? 'stale' : 'success',
        data: query.data,
        error: query.isError ? toDataStateError(query.error) : null,
        last_successful_fetch_at: lastSuccessAt,
        next_retry_at: null,
        source: query.isFetching ? 'fresh' : 'cache',
        ttl_seconds: ttlSeconds,
      };
    }

    return {
      status: 'idle',
      data: null,
      error: null,
      last_successful_fetch_at: null,
      next_retry_at: null,
      source: 'fresh',
      ttl_seconds: ttlSeconds,
    };
  }, [query.data, query.dataUpdatedAt, query.error, query.isError, query.isFetching, query.isPending, isEmpty, ttlSeconds]);

  return {
    state,
    refetch: () => void query.refetch(),
    isFetching: query.isFetching,
  };
}
